import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp } from "../components/Motion";
import { COLLECTIONS, GALLERY, CATEGORIES } from "../data/content";

export default function Collections() {
  const [cat, setCat] = useState("All");
  const filtered = cat === "All" ? GALLERY : GALLERY.filter((g) => g.cat === cat);

  return (
    <>
      <PageHeader
        overline="Collections & Portfolio"
        title={["Worn with", "grace."]}
        subtitle="A curated portfolio across Women, Men, Custom Creations and Festive & Occasion wear — every piece handcrafted in the Arkaari atelier."
      />

      {/* Collection feature rows */}
      <section className="max-w-[1500px] mx-auto px-5 sm:px-8 pb-8">
        {COLLECTIONS.map((c, i) => (
          <FadeUp key={c.id}>
            <div
              data-testid={`collection-row-${c.id}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center py-14 border-t border-[#D5D0C5] ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="img-frame rounded-sm">
                <img src={c.image} alt={c.title} className="w-full h-[64vh] object-cover rounded-sm" />
              </div>
              <div>
                <span className="font-serif text-5xl font-light text-[#C6A15B]/60">{c.index}</span>
                <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-3">{c.title}</h2>
                <p className="font-serif italic text-2xl text-[#C6A15B] mt-2">{c.tagline}</p>
                <p className="text-[#7a7568] text-lg leading-relaxed mt-6 max-w-md">{c.desc}</p>
                <div className="flex items-center gap-6 mt-8">
                  <span className="overline text-[#7a7568]">{c.pieces}</span>
                  <Link
                    to="/custom-orders"
                    className="overline link-underline inline-flex items-center gap-2"
                    data-testid={`collection-cta-${c.id}`}
                  >
                    Enquire / Customize <ArrowUpRight size={16} strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            </div>
          </FadeUp>
        ))}
      </section>

      {/* Gallery */}
      <section data-testid="gallery" className="max-w-[1500px] mx-auto px-5 sm:px-8 py-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">The Lookbook</h2>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`overline px-4 py-2 border transition-colors duration-300 ${
                  cat === c
                    ? "bg-[#121212] text-[#F5F2EA] border-[#121212]"
                    : "border-[#D5D0C5] text-[#7a7568] hover:border-[#121212] hover:text-[#121212]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
          <AnimatePresence mode="popLayout">
            {filtered.map((g, i) => (
              <motion.div
                key={g.src}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="mb-6 break-inside-avoid img-frame rounded-sm relative group"
              >
                <img src={g.src} alt={g.cat} className={`w-full object-cover rounded-sm ${g.tall ? "h-[70vh]" : "h-[46vh]"}`} />
                <span className="absolute bottom-4 left-4 overline text-white bg-black/40 backdrop-blur px-3 py-1">{g.cat}</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </section>
    </>
  );
}
